'use client';

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import { animate, useMotionValue, useTransform } from 'framer-motion';
import type { AnimationPlaybackControls, MotionValue } from 'framer-motion';

/** Drop distance past which releasing closes the lightbox. */
const DISMISS_DISTANCE_PX = 110;
/** A quick flick closes even below the distance threshold (px per ms). */
const DISMISS_VELOCITY = 0.5;
/** Chrome is fully faded before the dismiss threshold, so the intent reads early. */
const CHROME_FADE_DISTANCE_PX = 80;
const BACKDROP_MIN_OPACITY = 0.35;
const CONTENT_MIN_SCALE = 0.88;
/** Movement below this is still a tap, not a drag. */
const GESTURE_START_PX = 6;
/** Vertical must beat horizontal by this factor to steal the gesture from the carousel. */
const DIRECTION_LOCK_RATIO = 1.2;
/** Upward drags are resisted rather than tracked 1:1 — dismissal is downward only. */
const UPWARD_RESISTANCE = 0.3;

const SPRING_BACK = { type: 'spring', stiffness: 500, damping: 40, mass: 0.6 } as const;
const EXIT = { duration: 0.22, ease: [0.32, 0, 0.67, 0] } as const;

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

export interface LightboxSwipeDismissApi {
	/** Attach to the lightbox surface — gesture listeners bind here. */
	surfaceRef: RefObject<HTMLDivElement | null>;
	/** Vertical offset of the lightbox content, in px. */
	dragY: MotionValue<number>;
	/** Content shrinks slightly as it is dragged away. */
	contentScale: MotionValue<number>;
	/** 1 → 0 across the gesture; drives the toolbar/arrow fade-out. */
	chromeOpacity: MotionValue<number>;
	backdropOpacity: MotionValue<number>;
}

/**
 * A vertical drag anywhere inside the surface can only be a dismiss if it does not
 * belong to something else: a scrollable pane (PDF, text file), native media controls,
 * or an in-flight horizontal carousel swipe.
 */
const isGestureBlocked = (target: EventTarget | null, surface: Element): boolean => {
	if (!(target instanceof Element)) return false;
	if (target.closest('video, iframe, [data-no-swipe-dismiss]')) return true;
	let el: Element | null = target;
	while (el && el !== surface) {
		if (el.scrollHeight > el.clientHeight + 1) {
			const overflowY = getComputedStyle(el).overflowY;
			if (overflowY === 'auto' || overflowY === 'scroll') return true;
		}
		el = el.parentElement;
	}
	return false;
};

/**
 * Swipe-down-to-close for the attachment lightbox (touch only). The content tracks the
 * finger while the chrome and backdrop fade out proportionally; releasing past the
 * threshold plays the drop out and then closes, otherwise it springs back.
 *
 * Pass `enabled: false` while zoomed — a zoomed image owns vertical touch for panning.
 */
export const useLightboxSwipeDismiss = (
	enabled: boolean,
	onDismiss: () => void,
): LightboxSwipeDismissApi => {
	const surfaceRef = useRef<HTMLDivElement | null>(null);
	const dragY = useMotionValue(0);
	const progress = useTransform(dragY, (y) => clamp(y / DISMISS_DISTANCE_PX, 0, 1));
	const contentScale = useTransform(progress, [0, 1], [1, CONTENT_MIN_SCALE]);
	const backdropOpacity = useTransform(progress, [0, 1], [1, BACKDROP_MIN_OPACITY]);
	const chromeOpacity = useTransform(dragY, (y) => 1 - clamp(y / CHROME_FADE_DISTANCE_PX, 0, 1));

	const enabledRef = useRef(enabled);
	enabledRef.current = enabled;
	const onDismissRef = useRef(onDismiss);
	onDismissRef.current = onDismiss;

	useEffect(() => {
		const el = surfaceRef.current;
		if (!el) return;

		let gesture: {
			startX: number;
			startY: number;
			lastY: number;
			lastTime: number;
			velocity: number;
			axis: 'undecided' | 'vertical' | 'abandoned';
		} | null = null;
		let playback: AnimationPlaybackControls | null = null;

		const onStart = (e: TouchEvent) => {
			playback?.stop();
			playback = null;
			if (!enabledRef.current || e.touches.length !== 1 || isGestureBlocked(e.target, el)) {
				gesture = null;
				return;
			}
			const touch = e.touches[0];
			gesture = {
				startX: touch.clientX,
				startY: touch.clientY,
				lastY: touch.clientY,
				lastTime: e.timeStamp,
				velocity: 0,
				axis: 'undecided',
			};
		};

		const settleBack = () => {
			playback = animate(dragY, 0, SPRING_BACK);
		};

		const onMove = (e: TouchEvent) => {
			if (!gesture || gesture.axis === 'abandoned') return;
			// A second finger means pinch-zoom, not dismissal — hand the gesture back.
			if (e.touches.length !== 1 || !enabledRef.current) {
				const wasDragging = gesture.axis === 'vertical';
				gesture = null;
				if (wasDragging) settleBack();
				return;
			}

			const touch = e.touches[0];
			const dx = touch.clientX - gesture.startX;
			const dy = touch.clientY - gesture.startY;

			if (gesture.axis === 'undecided') {
				if (Math.hypot(dx, dy) < GESTURE_START_PX) return;
				// Mostly horizontal, or the carousel already started scrolling (no longer
				// cancelable) — leave it to the native scroll-snap track.
				if (Math.abs(dy) < Math.abs(dx) * DIRECTION_LOCK_RATIO || !e.cancelable) {
					gesture.axis = 'abandoned';
					return;
				}
				gesture.axis = 'vertical';
			}

			if (e.cancelable) e.preventDefault();
			const elapsed = e.timeStamp - gesture.lastTime;
			if (elapsed > 0) gesture.velocity = (touch.clientY - gesture.lastY) / elapsed;
			gesture.lastY = touch.clientY;
			gesture.lastTime = e.timeStamp;
			dragY.set(dy >= 0 ? dy : dy * UPWARD_RESISTANCE);
		};

		const onEnd = () => {
			if (!gesture) return;
			const wasDragging = gesture.axis === 'vertical';
			const { velocity } = gesture;
			gesture = null;
			if (!wasDragging) return;

			const y = dragY.get();
			const flungAway = y > GESTURE_START_PX && velocity > DISMISS_VELOCITY;
			if (y > DISMISS_DISTANCE_PX || flungAway) {
				playback = animate(dragY, el.clientHeight, {
					...EXIT,
					onComplete: () => onDismissRef.current(),
				});
				return;
			}
			settleBack();
		};

		el.addEventListener('touchstart', onStart, { passive: true });
		el.addEventListener('touchmove', onMove, { passive: false });
		el.addEventListener('touchend', onEnd);
		el.addEventListener('touchcancel', onEnd);
		return () => {
			playback?.stop();
			el.removeEventListener('touchstart', onStart);
			el.removeEventListener('touchmove', onMove);
			el.removeEventListener('touchend', onEnd);
			el.removeEventListener('touchcancel', onEnd);
		};
		// The surface element is created once with the lightbox and never swaps.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { surfaceRef, dragY, contentScale, chromeOpacity, backdropOpacity };
};
